import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Save, Send, Printer, CheckCircle, Loader2, Mail, MessageCircle, ChevronDown, Plus, X, RotateCcw } from 'lucide-react';

const InspeccionToolbar = ({
  loading,
  sendingEmail,
  sendingWhatsapp,
  inspeccion,
  isFormComplete,
  canSendOrPrint,
  canTerminar,
  datosInspeccion,
  onSave,
  onSendEmail,
  onSendWhatsApp,
  onPrint,
  onTerminar,
  onReactivar,
  onAddSolicitud,
  onClose,
  platformConfig,
  codigoRequerimiento,
  readOnly,
  formDirty,
  sealLoading,
  isAdmin,
  requerimientoEstado,
  todasNoProcede
}) => {
  const [showSendMenu, setShowSendMenu] = useState(false);
  const [showReactivarConfirm, setShowReactivarConfirm] = useState(false);
  const menuRef = useRef(null);
  
  const acta_estado = datosInspeccion?.actaEstado;
  const isActaAprobadaORechazada = ['aprobado_correo', 'rechazado_correo', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(acta_estado);
  const whatsappEnabled = platformConfig?.aprobar_acta_inspeccion_whatsapp === true;
  
  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSendMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isSending = sendingEmail || sendingWhatsapp;
  const isActaUploaded = !!(inspeccion?.acta_url) && !inspeccion.acta_url.includes('/acta-view');
  const isFileLocked = isActaUploaded || inspeccion?.estado === 'completada';
  const isLocked = 
    isFileLocked ||
    ['enviado_correo', 'aprobado_correo', 'rechazado_correo', 'enviado_whatsapp', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(acta_estado);
  
  // Reactivar: solo admin
  // Caso 1: requerimiento cerrado + acta rechazada
  // Caso 2: requerimiento terminado + todas las solicitudes son "no_procede"
  const isReactivarRechazada = requerimientoEstado === 'cerrado' && 
    ['rechazado_correo', 'rechazado_whatsapp'].includes(acta_estado);
  const isReactivarNoProcede = requerimientoEstado === 'terminado' && todasNoProcede;
  const canReactivar = isAdmin && (isReactivarRechazada || isReactivarNoProcede);
  
  const reactivarTooltip = isReactivarNoProcede
    ? 'Reactivar inspección: todas las solicitudes fueron evaluadas como "No Procede". Permite corregir la evaluación.'
    : 'Reactivar inspección rechazada por el testigo';
  const reactivarConfirmMsg = isReactivarNoProcede
    ? 'Se restaurará el requerimiento a "Inspección Programada" para re-evaluar las solicitudes marcadas como "No Procede".'
    : 'Se restaurará el requerimiento a "Inspección Programada" y se limpiará el estado del acta.';
  
  return (
    <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-3 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3" data-testid="inspeccion-toolbar-header">
      {/* Title */}
      <h2 className="text-sm sm:text-lg font-semibold text-[#2d2d2d] whitespace-nowrap mr-auto" data-testid="inspeccion-title">
        Realizar Inspección {codigoRequerimiento ? `- #${codigoRequerimiento}` : ''}
      </h2>

      {/* Buttons - right aligned, icon-only on mobile */}
      <div className="flex gap-1.5 sm:gap-2 items-center">
        {/* Save Button - hidden in readOnly */}
        {!readOnly && (
        <Button
          size="sm"
          className="bg-[#9d8272] hover:bg-[#8b7265] text-white text-xs h-8 px-2 sm:px-3"
          onClick={onSave}
          disabled={loading || !formDirty || !isFormComplete || isLocked}
          title={isLocked ? 'No se puede modificar después de enviar el acta' : !formDirty ? 'Sin cambios pendientes' : 'Grabar'}
          data-testid="inspeccion-save-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin sm:mr-1" /> : <Save size={14} className="sm:mr-1" />}
          <span className="hidden sm:inline">Grabar</span>
        </Button>
        )}

        {/* Add Solicitud Button - hidden in readOnly */}
        {!readOnly && onAddSolicitud && (
          <Button
            size="sm"
            variant="outline"
            className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs h-8 px-2 sm:px-3"
            onClick={onAddSolicitud}
            title="Agregar Solicitud"
            data-testid="inspeccion-add-solicitud-btn"
          >
            <Plus size={14} className="sm:mr-1" />
            <span className="hidden sm:inline">Solicitud</span>
          </Button>
        )}

        {/* Send Button - hidden in readOnly */}
        {!readOnly && (whatsappEnabled ? (
          <div className="relative" ref={menuRef}>
            <Button
              size="sm"
              variant="outline"
              className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs h-8 px-2 sm:px-3"
              disabled={isSending || !canSendOrPrint || isActaAprobadaORechazada || isFileLocked}
              onClick={() => setShowSendMenu(!showSendMenu)}
              title={isFileLocked ? 'Acta adjunta, no se puede enviar' : isActaAprobadaORechazada ? 'Acta ya aprobada/rechazada' : 'Enviar acta al testigo'}
              data-testid="inspeccion-send-dropdown-btn"
            >
              {isSending ? (
                <Loader2 size={14} className="animate-spin sm:mr-1" />
              ) : (
                <Send size={14} className="sm:mr-1" />
              )}
              <span className="hidden sm:inline">Enviar</span>
              <ChevronDown size={12} className="ml-0.5 sm:ml-1" />
            </Button>
            
            {showSendMenu && (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] overflow-hidden">
                <button
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                  onClick={() => { setShowSendMenu(false); onSendEmail(); }}
                  disabled={sendingEmail}
                  data-testid="send-by-email-option"
                >
                  <Mail size={15} className="text-blue-600" />
                  <span>Correo</span>
                </button>
                <div className="border-t border-gray-100" />
                <button
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 text-left transition-colors"
                  onClick={() => { setShowSendMenu(false); onSendWhatsApp(); }}
                  disabled={sendingWhatsapp}
                  data-testid="send-by-whatsapp-option"
                >
                  <MessageCircle size={15} className="text-green-600" />
                  <span>WhatsApp</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs h-8 px-2 sm:px-3"
            disabled={sendingEmail || !canSendOrPrint || isActaAprobadaORechazada || isFileLocked}
            onClick={onSendEmail}
            title={isFileLocked ? 'Acta adjunta, no se puede enviar' : isActaAprobadaORechazada ? 'Acta ya aprobada/rechazada' : 'Enviar por correo'}
            data-testid="inspeccion-send-btn"
          >
            {sendingEmail ? (
              <Loader2 size={14} className="animate-spin sm:mr-1" />
            ) : (
              <Send size={14} className="sm:mr-1" />
            )}
            <span className="hidden sm:inline">Enviar</span>
          </Button>
        ))}

        {/* Print Button */}
        <Button
          size="sm"
          variant="outline"
          className="border-[#9d8272] text-[#9d8272] hover:bg-[#9d8272] hover:text-white text-xs h-8 px-2 sm:px-3"
          onClick={onPrint}
          disabled={!canSendOrPrint || sealLoading || (isActaUploaded && inspeccion?.estado !== 'completada')}
          title={sealLoading ? 'Cargando sello...' : isActaUploaded && inspeccion?.estado !== 'completada' ? 'Acta adjunta' : 'Imprimir'}
          data-testid="inspeccion-print-btn"
        >
          {sealLoading ? <Loader2 size={14} className="sm:mr-1 animate-spin" /> : <Printer size={14} className="sm:mr-1" />}
          <span className="hidden sm:inline">{sealLoading ? 'Cargando...' : 'Imprimir'}</span>
        </Button>

        {/* Terminar Button - hidden in readOnly */}
        {!readOnly && (
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-2 sm:px-3"
          disabled={loading || !canTerminar || isActaAprobadaORechazada}
          onClick={onTerminar}
          title={!canTerminar ? 'Debe enviar el acta y que sea aprobada primero' : 'Terminar'}
          data-testid="inspeccion-terminar-btn"
        >
          <CheckCircle size={14} className="sm:mr-1" />
          <span className="hidden sm:inline">Terminar</span>
        </Button>
        )}

        {/* Reactivar Button - solo admin + (cerrado+rechazada OR terminado+todasNoProcede) */}
        {canReactivar && (
          <div className="relative">
            {!showReactivarConfirm ? (
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-8 px-2 sm:px-3"
                onClick={() => setShowReactivarConfirm(true)}
                disabled={loading}
                title={reactivarTooltip}
                data-testid="inspeccion-reactivar-btn"
              >
                <RotateCcw size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">Reactivar</span>
              </Button>
            ) : (
              <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 min-w-[260px]">
                <p className="text-sm font-medium text-gray-800 mb-1">¿Confirmar reactivación?</p>
                <p className="text-xs text-gray-500 mb-3">{reactivarConfirmMsg}</p>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowReactivarConfirm(false)} data-testid="reactivar-cancel-btn">
                    Cancelar
                  </Button>
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-7" onClick={() => { setShowReactivarConfirm(false); onReactivar(); }} disabled={loading} data-testid="reactivar-confirm-btn">
                    {loading ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                    Confirmar
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-800 ml-1 sm:ml-2 transition-colors"
            title="Cerrar"
            data-testid="inspeccion-close-btn"
          >
            <X size={18} />
            <span className="hidden sm:inline text-sm">Cerrar</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default InspeccionToolbar;
